import { delimited } from '@/common/utils/commonUtil';

// エンジェル税制シミュレーターの計算ロジック。
// 令和7年12月時点の税制に基づく。所得控除は基礎控除のみ、譲渡益は申告分離課税を前提とする。
// 戻り値は切り捨て前の生の値（小数を含み得る）。表示値への変換は toDisplayAmount で行う。

export interface AngelTaxInput {
  // 総所得金額
  income: number;
  // エンジェル税制適用会社への投資額
  investment: number;
  // 上場株式の売買による譲渡益
  gain: number;
  // 非上場株式の売買による譲渡益
  gainByOutsideStock: number;
}

export interface AngelTax {
  deduction: number;
  incomeTax: number;
  refund: number;
}

export interface AngelTaxResult {
  // エンジェル税制未使用の場合の所得税額
  incomeTax: number;
  angelTaxA: AngelTax;
  angelTaxB: AngelTax;
  angelTaxPreSeed: AngelTax;
}

// 株式譲渡益に対する税率(復興特別所得税含む)
const S_TAX = 0.15315;
// 復興特別所得税率
const TAX_FOR_RECONSTRUCTION = 0.021;
// 基礎控除最大値
const MAX_BASE_DEDUCTION = 950000;

function lookUpTaxPercentage(income: number): number {
  if (income <= 1950000) {
    return 0.05;
  } else if (1950000 < income && income <= 3300000) {
    return 0.1;
  } else if (3300000 < income && income <= 6950000) {
    return 0.2;
  } else if (6950000 < income && income <= 9000000) {
    return 0.23;
  } else if (9000000 < income && income <= 18000000) {
    return 0.33;
  } else if (18000000 < income && income <= 40000000) {
    return 0.4;
  } else {
    return 0.45;
  }
}

function calculateDeduction(tax: number): number {
  if (tax <= 0.05) {
    return 0;
  } else if (tax === 0.1) {
    return 97500;
  } else if (tax === 0.2) {
    return 427500;
  } else if (tax === 0.23) {
    return 636000;
  } else if (tax === 0.33) {
    return 1536000;
  } else if (tax === 0.4) {
    return 2796000;
  } else if (tax === 0.45) {
    return 4796000;
  } else {
    return 0;
  }
}

const roundForTax = (sourceNumber: number): number =>
  Math.floor(sourceNumber / 100) * 100;

const baseDeductionByIncome = (income: number): number => {
  if (income <= MAX_BASE_DEDUCTION) {
    return income;
  } else if (income <= 1320000) {
    return MAX_BASE_DEDUCTION;
  } else if (income <= 3360000) {
    return 880000;
  } else if (income <= 4890000) {
    return 680000;
  } else if (income <= 6550000) {
    return 630000;
  } else if (income <= 23500000) {
    return 580000;
  } else if (income <= 24000000) {
    return 480000;
  } else if (income <= 24500000) {
    return 320000;
  } else if (income <= 25000000) {
    return 160000;
  } else {
    return 0;
  }
};

// エンジェル税制未使用の場合の所得税額
function calculateIncomeTax(input: AngelTaxInput): number {
  const incomeVal = input.income;
  const gainTotal = input.gain + input.gainByOutsideStock;
  const taxPercentage = lookUpTaxPercentage(
    incomeVal - baseDeductionByIncome(incomeVal),
  );
  const deduction = calculateDeduction(taxPercentage);
  const taxAfterDeduction = Math.max(
    roundForTax(
      (incomeVal - baseDeductionByIncome(incomeVal)) * taxPercentage -
        deduction,
    ),
    0,
  );
  const wkAmount = taxAfterDeduction * (TAX_FOR_RECONSTRUCTION * 1000);
  const reconstructionTax = wkAmount / 1000;
  return Math.max(
    taxAfterDeduction +
      roundForTax(
        (gainTotal - Math.max(MAX_BASE_DEDUCTION - incomeVal, 0)) * S_TAX,
      ) +
      reconstructionTax,
    0,
  );
}

// 優遇措置A（投資額を総所得金額から控除）
function calculateAngelTaxA(input: AngelTaxInput, incomeTax: number): AngelTax {
  const incomeVal = input.income;
  const investmentVal = input.investment;
  const gainTotal = input.gain + input.gainByOutsideStock;
  const baseDeduction = baseDeductionByIncome(incomeVal);
  // 基礎控除のみ引いて判定用
  const incomeExceptBaseDeduction = Math.max(incomeVal - baseDeduction, 0);
  const deductionA = Math.max(
    Math.min(investmentVal, 8000000, incomeVal * 0.4) - 2000,
    0,
  );
  // 基礎控除＆優遇措置Aを引いて判定用
  const incomeExceptDeductionAandBase = Math.max(
    incomeExceptBaseDeduction - deductionA,
    0,
  );
  // 税率
  const taxPercentage = lookUpTaxPercentage(incomeExceptDeductionAandBase);
  const deduction = calculateDeduction(taxPercentage);
  // 譲渡益を除いた所得税(復興税未加算)
  const incomeTaxExceptGailTotal =
    roundForTax(incomeExceptDeductionAandBase * taxPercentage) - deduction;
  // 譲渡益から基礎控除の残りを引いた額は 0 未満にしない（B と同じ）。負のままだと所得税が負になり、
  // 税金 0 円の人にも節税効果が出てしまう
  const incomeTaxA =
    incomeTaxExceptGailTotal +
    roundForTax(
      Math.max(gainTotal - Math.max(MAX_BASE_DEDUCTION - incomeVal, 0), 0) *
        S_TAX,
    );
  const reconstructionTax = Math.floor(
    incomeTaxExceptGailTotal * TAX_FOR_RECONSTRUCTION,
  );
  return {
    deduction: deductionA,
    incomeTax: incomeTaxA + reconstructionTax,
    refund: Math.max(incomeTax - (incomeTaxA + reconstructionTax), 0),
  };
}

// 優遇措置B（投資額を株式譲渡益から控除）
function calculateAngelTaxB(input: AngelTaxInput, incomeTax: number): AngelTax {
  const incomeVal = input.income;
  const investmentVal = input.investment;
  const gainTotal = input.gain + input.gainByOutsideStock;
  const baseDeduction = baseDeductionByIncome(incomeVal);
  // 基礎控除のみ引いて判定用
  const incomeExceptBaseDeduction = Math.max(incomeVal - baseDeduction, 0);
  // 税率
  const taxPercentage = lookUpTaxPercentage(incomeExceptBaseDeduction);
  const deduction = calculateDeduction(taxPercentage);
  const deductionB = Math.min(
    Math.min(investmentVal, gainTotal) +
      (incomeVal <= MAX_BASE_DEDUCTION ? MAX_BASE_DEDUCTION - incomeVal : 0),
    gainTotal,
  );
  const incomeTaxExceptGailTotal =
    roundForTax(incomeExceptBaseDeduction * taxPercentage) - deduction;
  const reconstructionTax = Math.floor(
    incomeTaxExceptGailTotal * TAX_FOR_RECONSTRUCTION,
  );
  const incomeTaxB =
    incomeTaxExceptGailTotal +
    roundForTax(
      Math.max(
        gainTotal - investmentVal - Math.max(MAX_BASE_DEDUCTION - incomeVal, 0),
        0,
      ) * S_TAX,
    ) +
    reconstructionTax;
  return {
    deduction: deductionB,
    incomeTax: incomeTaxB,
    refund: Math.max(incomeTax - incomeTaxB, 0),
  };
}

// プレシード・シード特例。投資時点は優遇措置 B と同じく投資額の全額を株式譲渡益から控除する。
// 年間 20 億円は株式譲渡時点で非課税になる部分の上限で、超える分は B と同じ課税繰延になる（経産省・自社の解説ページ）
function calculateAngelTaxPreSeed(
  input: AngelTaxInput,
  incomeTax: number,
): AngelTax {
  return calculateAngelTaxB(input, incomeTax);
}

// 生の計算値を表示値にする（切り捨て）
export const toDisplayAmount = (value: number): number => Math.floor(value);

// 生の計算値を、3 桁区切りに円を付けた表示にする
export const toDisplayYen = (value: number): string =>
  `${delimited(toDisplayAmount(value))}円`;

export function calculateAngelTax(input: AngelTaxInput): AngelTaxResult {
  const incomeTax = calculateIncomeTax(input);
  return {
    incomeTax,
    angelTaxA: calculateAngelTaxA(input, incomeTax),
    angelTaxB: calculateAngelTaxB(input, incomeTax),
    angelTaxPreSeed: calculateAngelTaxPreSeed(input, incomeTax),
  };
}
